import { useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { Search, ImageIcon } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"

type PickerImage = {
  id: number
  alt_text: string
  caption: string | null
  thumb_url: string | null
  used_by: string[]
}

type Props = {
  images: PickerImage[]
  attachable_type: string
  attachable_id: number
  return_to: string | null
  search: string | null
}

export default function AdminImageAttachmentsNew({
  images,
  attachable_type,
  attachable_id,
  return_to,
  search,
}: Props) {
  const [ query, setQuery ] = useState(search ?? "")

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    router.get("/admin/image_attachments/new", {
      attachable_type,
      attachable_id,
      return_to,
      search: query || undefined,
    }, { replace: true })
  }

  function handleAttach(imageId: number) {
    router.post("/admin/image_attachments", {
      image_id:        imageId,
      attachable_type,
      attachable_id,
      return_to,
    })
  }

  const entityLabel = attachable_type.toLowerCase()

  return (
    <>
      <Head title={`Attach image to ${entityLabel}`}>
        <meta name="description" content={`Choose an existing image to attach to this ${entityLabel}.`} />
        <meta property="og:title" content={`Attach image to ${entityLabel}`} />
        <meta property="og:description" content={`Choose an existing image to attach to this ${entityLabel}.`} />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex items-center gap-2 text-xs text-ink-muted mb-1">
                {return_to && (
                  <>
                    <Link href={return_to} className="hover:text-amber-600 dark:hover:text-amber-400 no-underline capitalize">{entityLabel}</Link>
                    <span>/</span>
                  </>
                )}
                <span>Attach existing image</span>
              </div>
              <h1>Attach existing image</h1>
              <p className="mt-1 text-ink-muted">
                Choose an image from the library to attach to this {entityLabel}.
              </p>
            </div>

            {return_to && (
              <Link
                href={return_to}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-700 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 rounded-lg transition-colors no-underline shrink-0"
              >
                Cancel
              </Link>
            )}
          </div>
        </div>

        <div className="mt-6 space-y-6">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex gap-2 max-w-sm">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by caption, alt text, or entity name…"
                className="w-full pl-9 pr-3 py-2 text-sm bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-900 dark:text-white placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-colors"
              />
            </div>
            <button
              type="submit"
              className="px-3 py-2 text-xs font-medium text-stone-700 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 rounded-lg transition-colors"
            >
              Search
            </button>
          </form>

          {/* Grid */}
          {images.length === 0 ? (
            <div className="py-16 text-center">
              <ImageIcon className="w-8 h-8 text-stone-300 dark:text-stone-700 mx-auto mb-3" strokeWidth={1} />
              <p className="text-sm text-stone-500 dark:text-stone-400">
                {search ? "No images match your search." : "No other images in the library yet."}
              </p>
              {search && (
                <button
                  onClick={() => { setQuery(""); router.get("/admin/image_attachments/new", { attachable_type, attachable_id, return_to }, { replace: true }) }}
                  className="mt-3 text-xs text-amber-600 dark:text-amber-400 hover:underline"
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <>
              <p className="text-xs text-stone-500 dark:text-stone-400 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                {images.length} image{images.length !== 1 ? "s" : ""}{search ? ` matching "${search}"` : ""}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {images.map((img) => (
                  <button
                    key={img.id}
                    onClick={() => handleAttach(img.id)}
                    className="group text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded-xl"
                    title={img.alt_text || img.caption || "Attach this image"}
                  >
                    <div className="aspect-square rounded-xl overflow-hidden bg-stone-100 dark:bg-stone-800 relative ring-2 ring-transparent group-hover:ring-amber-400 transition-all">
                      {img.thumb_url ? (
                        <img
                          src={img.thumb_url}
                          alt={img.alt_text}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="w-8 h-8 text-stone-300 dark:text-stone-600" strokeWidth={1} />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-amber-500/0 group-hover:bg-amber-500/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <span className="text-xs font-semibold text-amber-700 dark:text-amber-300 bg-white/90 dark:bg-stone-900/90 px-2 py-1 rounded-md shadow">
                          Attach
                        </span>
                      </div>
                    </div>
                    <div className="mt-1.5 px-0.5 space-y-0.5">
                      {(img.alt_text || img.caption) && (
                        <p className="text-xs text-stone-500 dark:text-stone-400 truncate">
                          {img.alt_text || img.caption}
                        </p>
                      )}
                      {img.used_by.length > 0 && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 truncate" title={img.used_by.join(", ")}>
                          {img.used_by.slice(0, 2).join(", ")}{img.used_by.length > 2 ? ` +${img.used_by.length - 2}` : ""}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </AdminShell>
    </>
  )
}
